import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
} from 'recharts';
import { serviceHealthApi } from '../lib/api.js';
import type { PropertyRate, FeatureAvailability, PurchaseConversion } from '../lib/api.js';
import { ComingSoon } from '../components/ComingSoon.js';
import { queryKeys } from '../lib/query-keys.js';
import { formatNumber, formatDate } from '../lib/formatters.js';
import { useDateRange } from '../hooks/useDateRange.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business サービスヘルスページ。機能別利用可能率トレンド・プロパティ率テーブル・購入コンバージョン統計を可視化
 * @graph-connects serviceHealthApi [calls] ServiceHealth APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects useDateRange [calls] 日付範囲管理フック
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDate [calls] 日付フォーマット
 * @graph-connects ComingSoon [calls] Coming Soonオーバーレイ描画
 * @graph-connects DateRangeSelector [calls] 日付範囲セレクター描画
 * @graph-connects AvailabilityPanel [calls] 機能別利用可能率パネル描画
 * @graph-connects PropertyCard [calls] プロパティカード描画
 * @graph-connects PurchaseConversionChart [calls] 購入コンバージョン円グラフ描画
 * @graph-connects PropertyTrend [calls] プロパティトレンドグラフ描画
 * @graph-connects formatPropertyName [calls] プロパティ名日本語ラベル変換
 * @graph-connects isPositiveProperty [calls] ポジティブプロパティ判定
 */
export const Route = createFileRoute('/service-health')({
  component: ServiceHealthPage,
});

/** プロパティ名を日本語ラベルに変換 */
/** @graph-connects none */
const PROPERTY_LABELS: Record<string, string> = {
  // 本体アイテム（購入済み含む利用可能率）
  is_buyable: '購入可能',
  // 色違いアイテム（dc_ = diff color）
  dc_is_buyable: '色違い購入可能',
  dc_is_out_of_stock: '色違い在庫切れ',
  dc_is_sold_out: '色違い売切',
  dc_is_purchased: '色違い購入済',
  // 新品購入可能
  new_item_is_buyable: '新品購入可能（現品）',
  dc_new_item_is_buyable: '新品購入可能（色違い）',
  // オプション変更
  is_disabled_all: '全制限',
  is_able_to_accessory: 'アクセサリー可能',
  is_able_to_one_select: 'ワンセレクト可能',
  is_double_box_available: 'ダブルボックス可能',
  is_one_select_blocked_by_brand: 'ブランドブロック',
  is_one_select_blocked_by_occasion: 'オケージョンブロック',
  is_one_select_blocked_by_plus_size: 'プラスサイズブロック',
  // プラン変更
  is_locked: 'ロック中',
  has_pending_plan_change: 'プラン変更保留',
  has_pending_course_change: 'コース変更保留',
  // 返却
  can_return_on_sagawa: '佐川利用可能',
  has_last_send_back_method: '返却履歴あり',
  // スタイリスト指名
  is_premium: 'プレミアム',
  has_designated_stylist: '指名スタイリストあり',
};

/** @graph-connects none */
function formatPropertyName(prop: string): string {
  return PROPERTY_LABELS[prop] ?? prop;
}

/** ポジティブなプロパティかどうか（trueRate が高い方が良い） */
/** @graph-connects none */
function isPositiveProperty(prop: string): boolean {
  const negative = [
    'dc_is_out_of_stock',
    'dc_is_sold_out',
    'is_disabled_all',
    'is_one_select_blocked_by_brand',
    'is_one_select_blocked_by_occasion',
    'is_one_select_blocked_by_plus_size',
    'is_locked',
    'has_pending_plan_change',
    'has_pending_course_change',
  ];
  return !negative.includes(prop);
}

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function ServiceHealthPage() {
  const { t } = useTranslation();
  const { preset, setPreset, startDate, endDate, setCustomDates } = useDateRange('30d');

  const availQuery = useQuery({
    queryKey: queryKeys.serviceHealth.availability(startDate, endDate),
    queryFn: () => serviceHealthApi.getAvailability(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('nav.serviceHealth')}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            サービス全体の機能利用可能状態を可視化
          </p>
        </div>
        <DateRangeSelector
          preset={preset}
          startDate={startDate}
          endDate={endDate}
          onPresetChange={setPreset}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* Availability パネル */}
      {availQuery.isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card">
              <div className="skeleton-shimmer h-48 w-full rounded-lg" />
            </div>
          ))}
        </div>
      ) : availQuery.data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {availQuery.data.summary.map((feature) => {
            const panel = (
              <AvailabilityPanel
                key={feature.feature}
                feature={feature}
                trends={availQuery.data.trends.filter((tr) => tr.feature === feature.feature)}
              />
            );
            const comingSoonFeatures = ['返却', 'スタイリスト指名'];
            if (comingSoonFeatures.includes(feature.feature)) {
              return <ComingSoon key={feature.feature}>{panel}</ComingSoon>;
            }
            return panel;
          })}
        </div>
      ) : null}
    </div>
  );
}

// ============================================================
// Availability パネル
// ============================================================

/** @graph-connects none */
const DONUT_COLORS = ['var(--color-accent-primary)', 'var(--color-error)'];
/** @graph-connects none */
const DONUT_COLORS_NEG = ['var(--color-error)', 'var(--chart-6)'];

/** @graph-connects none */
function AvailabilityPanel({
  feature,
  trends,
}: {
  feature: FeatureAvailability;
  trends: { date: string; property: string; trueRate: number }[];
}) {
  return (
    <div className="card animate-fade-in-up">
      <div className="flex items-center justify-between mb-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {feature.feature}
          </h3>
          {feature.path && (
            <p className="text-[10px] text-[var(--color-text-muted)] font-mono truncate">
              {feature.path}
            </p>
          )}
        </div>
        {feature.dataAvailableFrom && (
          <span className="text-[10px] text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-2 py-0.5 rounded shrink-0">
            {feature.dataAvailableFrom}〜
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {feature.properties.map((prop) => (
          <PropertyCard key={prop.property} prop={prop} />
        ))}
      </div>

      {/* 購入パネルはコンバージョン円グラフ、それ以外はトレンドライン */}
      {feature.purchaseConversion.length > 0 ? (
        <PurchaseConversionChart data={feature.purchaseConversion} />
      ) : feature.properties.length > 0 ? (
        <PropertyTrend
          trends={trends.filter((tr) => tr.property === feature.properties[0].property)}
          property={feature.properties[0].property}
        />
      ) : null}
    </div>
  );
}

/** @graph-connects none */
function PropertyCard({ prop }: { prop: PropertyRate }) {
  const positive = isPositiveProperty(prop.property);
  const rate = prop.trueRate;
  const isGood = positive ? rate >= 50 : rate < 20;
  const isBad = positive ? rate < 20 : rate >= 50;

  const colors = positive ? DONUT_COLORS : DONUT_COLORS_NEG;
  const donutData = [
    { name: 'true', value: prop.trueCount, fill: colors[0], fillOpacity: 0.9 },
    { name: 'false', value: prop.falseCount, fill: colors[1], fillOpacity: 0.3 },
  ];

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-bg-hover)]">
      <div className="w-10 h-10 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={donutData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={12}
              outerRadius={18}
              startAngle={90}
              endAngle={-270}
              strokeWidth={0}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="min-w-0">
        <p className="text-[10px] text-[var(--color-text-muted)] truncate">
          {formatPropertyName(prop.property)}
        </p>
        <p
          className={`text-sm font-bold font-mono ${
            isGood
              ? 'text-[var(--color-success)]'
              : isBad
                ? 'text-[var(--color-error)]'
                : 'text-[var(--color-text-primary)]'
          }`}
        >
          {String(rate)}%
        </p>
        <p className="text-[9px] text-[var(--color-text-muted)] font-mono">
          {formatNumber(prop.trueCount)} / {formatNumber(prop.totalCount)}
        </p>
      </div>
    </div>
  );
}

/** @graph-connects none */
const PURCHASE_TYPE_COLORS: Record<string, string> = {
  現品: 'var(--chart-1)',
  色違い: 'var(--chart-2)',
  新品: 'var(--chart-3)',
  過去アイテム: 'var(--chart-4)',
};

/** @graph-connects none */
function PurchaseConversionChart({ data }: { data: PurchaseConversion[] }) {
  const total = data.reduce((sum, d) => sum + d.events, 0);
  if (total === 0) return null;

  const pieData = data.map((d) => ({
    name: d.purchaseType,
    value: d.events,
    users: d.users,
    fill: PURCHASE_TYPE_COLORS[d.purchaseType] ?? 'var(--chart-4)',
    fillOpacity: 0.85,
  }));

  return (
    <div>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={24}
                outerRadius={40}
                startAngle={90}
                endAngle={-270}
                strokeWidth={0}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '11px',
                }}
                formatter={
                  ((value: number, name: string) => [
                    `${formatNumber(value)}件 (${String(Math.round((value / total) * 1000) / 10)}%)`,
                    name,
                  ]) as never
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1.5 flex-1">
          {data.map((d) => {
            const pct = Math.round((d.events / total) * 1000) / 10;
            return (
              <div key={d.purchaseType} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: PURCHASE_TYPE_COLORS[d.purchaseType] ?? 'var(--chart-4)' }}
                />
                <span className="text-xs text-[var(--color-text-secondary)] w-10 shrink-0">
                  {d.purchaseType}
                </span>
                <span className="text-xs font-mono text-[var(--color-text-primary)]">
                  {formatNumber(d.events)}件
                </span>
                <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
                  ({String(pct)}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** @graph-connects none */
function PropertyTrend({
  trends,
  property,
}: {
  trends: { date: string; trueRate: number }[];
  property: string;
}) {
  if (trends.length === 0) return null;

  const sorted = [...trends].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="mt-2 pt-2 border-t border-[var(--color-border)]">
      <p className="text-[10px] text-[var(--color-text-muted)] mb-1">
        {formatPropertyName(property)} 推移
      </p>
      <div className="h-20">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sorted} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
            <defs>
              <linearGradient id={`trend-${property}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-accent-primary)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-accent-primary)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" hide />
            <YAxis domain={[0, 100]} hide />
            <Tooltip
              contentStyle={{
                background: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '11px',
              }}
              formatter={
                ((value: number) => [`${String(value)}%`, formatPropertyName(property)]) as never
              }
              labelFormatter={((label: string) => formatDate(label)) as never}
            />
            <Area
              type="monotone"
              dataKey="trueRate"
              stroke="var(--color-accent-primary)"
              fill={`url(#trend-${property})`}
              strokeWidth={1.5}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
